import { useEffect, useRef, useState } from 'react';
import type { ProductSummaryDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { money } from '@/lib/format';
import { SearchIcon } from '@/components/icons';
import { Alert, Input, Skeleton, cx } from '@/components/ui';

/**
 * Choosing products by name instead of by id.
 *
 * Everywhere else in this admin, an API-backed list searches on the server with
 * debounced input and loads more on demand — the platform's rule, because a
 * catalogue is thousands of rows and fetch-all-and-filter stops working at
 * exactly the size that matters. This is the component that finally applies it
 * to products, which had been left as a comma-separated field of UUIDs.
 *
 * Chosen products are shown as chips **resolved by name**, including ones the
 * current search does not match: a campaign that already targets a product
 * should not appear to have lost it because someone typed a different word.
 */
export function ProductPicker({
  selectedIds,
  onChange,
  label,
  hint,
  ordered = false,
}: {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  label: string;
  hint?: string;
  /**
   * When the order is the point — a hand-picked rail (R7): the chosen products
   * become a numbered list the operator can move up and down, in place of chips.
   */
  ordered?: boolean;
}) {
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  const [search, setSearch] = useState('');
  const [results, setResults] = useState<ProductSummaryDto[]>([]);
  const [chosen, setChosen] = useState<ProductSummaryDto[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced search. The cleanup cancels a keystroke's request when the next
  // one arrives, so a fast typist does not race their own results.
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    let cancelled = false;

    timer.current = setTimeout(() => {
      setLoading(true);

      void withSession(() => adminApi.catalog.products({ search: search || undefined, limit: 10 }))
        .then((page) => {
          if (cancelled) return;
          setResults(page.items);
          setNextCursor(page.meta.nextCursor);
        })
        .catch((cause) => !cancelled && setError(errorMessage(cause, 'Could not search products.')))
        .finally(() => !cancelled && setLoading(false));
    }, 300);

    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [search]);

  /*
   * Resolve the names of anything already chosen.
   *
   * Without this the editor would show a list of UUIDs for the products it was
   * opened with — which is the problem this component exists to solve.
   */
  useEffect(() => {
    const missing = selectedIds.filter((id) => !chosen.some((product) => product.id === id));
    if (missing.length === 0) return;

    let cancelled = false;

    void Promise.all(missing.map((id) => withSession(() => adminApi.catalog.product(id)).catch(() => null)))
      .then((loaded) => {
        if (cancelled) return;
        setChosen((current) => [
          ...current,
          ...loaded.filter((product): product is NonNullable<typeof product> => product !== null),
        ]);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedIds, chosen]);

  const loadMore = () => {
    if (!nextCursor) return;
    setLoading(true);

    void withSession(() =>
      adminApi.catalog.products({ search: search || undefined, limit: 10, cursor: nextCursor }),
    )
      .then((page) => {
        setResults((current) => [...current, ...page.items]);
        setNextCursor(page.meta.nextCursor);
      })
      .catch((cause) => setError(errorMessage(cause, 'Could not load more products.')))
      .finally(() => setLoading(false));
  };

  const toggle = (product: ProductSummaryDto) => {
    setChosen((current) =>
      current.some((entry) => entry.id === product.id) ? current : [...current, product],
    );

    onChange(
      selectedIds.includes(product.id)
        ? selectedIds.filter((id) => id !== product.id)
        : [...selectedIds, product.id],
    );
  };

  const selected = selectedIds
    .map((id) => chosen.find((product) => product.id === id))
    .filter((product): product is ProductSummaryDto => product !== undefined);

  const move = (from: number, to: number) => {
    const next = [...selectedIds];
    const [entry] = next.splice(from, 1);
    next.splice(to, 0, entry);
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-ink">{label}</span>
      {hint ? <span className="-mt-1 text-xs text-ink-muted">{hint}</span> : null}

      {error ? <Alert>{error}</Alert> : null}

      {/* What is chosen, first: it is the answer to "what does this apply to?" */}
      {ordered && selectedIds.length > 0 ? (
        <ol className="flex flex-col divide-y divide-line rounded-control border border-line">
          {selectedIds.map((id, index) => (
            <li key={id} className="flex items-center gap-2 px-3 py-1.5 text-sm">
              <span data-numeric className="w-5 text-ink-faint">
                {index + 1}
              </span>
              <span className="min-w-0 flex-1 truncate text-ink">
                {/* A product deleted since keeps its place, visibly, until removed. */}
                {chosen.find((product) => product.id === id)?.name ?? '…'}
              </span>
              <button
                type="button"
                aria-label={t('moveUp')}
                disabled={index === 0}
                onClick={() => move(index, index - 1)}
                className="px-1.5 text-ink-muted transition-colors hover:text-ink disabled:opacity-30"
              >
                ↑
              </button>
              <button
                type="button"
                aria-label={t('moveDown')}
                disabled={index === selectedIds.length - 1}
                onClick={() => move(index, index + 1)}
                className="px-1.5 text-ink-muted transition-colors hover:text-ink disabled:opacity-30"
              >
                ↓
              </button>
              <button
                type="button"
                aria-label={t('remove')}
                onClick={() => onChange(selectedIds.filter((entry) => entry !== id))}
                className="px-1.5 text-ink-muted transition-colors hover:text-danger"
              >
                ×
              </button>
            </li>
          ))}
        </ol>
      ) : selected.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {selected.map((product) => (
            <li
              key={product.id}
              className="flex items-center gap-2 rounded-full border border-brand-600 bg-brand-50 px-3 py-1 text-xs text-brand-700"
            >
              <span className="max-w-56 truncate">{product.name}</span>
              <button
                type="button"
                aria-label={t('remove')}
                onClick={() => onChange(selectedIds.filter((id) => id !== product.id))}
                className="text-brand-700/70 transition-colors hover:text-danger"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 start-3 flex items-center text-ink-faint">
          <SearchIcon className="size-4" />
        </span>
        <Input
          type="search"
          value={search}
          placeholder={t('searchProducts')}
          onChange={(event) => setSearch(event.target.value)}
          className="ps-9"
        />
      </div>

      <div className="max-h-64 overflow-y-auto rounded-control border border-line">
        {loading && results.length === 0 ? (
          <div className="p-3">
            <Skeleton className="h-24" />
          </div>
        ) : results.length === 0 ? (
          <p className="p-3 text-sm text-ink-muted">{t('noProductsFound')}</p>
        ) : (
          <ul className="flex flex-col divide-y divide-line">
            {results.map((product) => {
              const isOn = selectedIds.includes(product.id);

              return (
                <li key={product.id}>
                  <button
                    type="button"
                    onClick={() => toggle(product)}
                    className={cx(
                      'flex w-full items-center gap-3 px-3 py-2 text-start transition-colors',
                      isOn ? 'bg-brand-50' : 'hover:bg-surface-sunken',
                    )}
                  >
                    <input type="checkbox" checked={isOn} readOnly className="size-4 shrink-0" />

                    {product.imageUrl ? (
                      <img
                        src={product.imageUrl}
                        alt=""
                        className="size-8 shrink-0 rounded-control object-cover"
                      />
                    ) : (
                      <span className="size-8 shrink-0 rounded-control bg-surface-sunken" />
                    )}

                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-ink">{product.name}</span>
                      <span data-numeric dir="ltr" className="block text-[11px] text-ink-faint">
                        {product.slug}
                      </span>
                    </span>

                    <span data-numeric className="shrink-0 text-xs text-ink-muted">
                      {money(product.price, locale, product.currency)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {nextCursor ? (
          <button
            type="button"
            onClick={loadMore}
            disabled={loading}
            className="w-full border-t border-line px-3 py-2 text-sm text-ink-muted transition-colors hover:bg-surface-sunken"
          >
            {loading ? t('loading') : t('loadMore')}
          </button>
        ) : null}
      </div>
    </div>
  );
}
